import { useQuery } from '@tanstack/react-query';

import { catalogApi } from 'api/catalog';

export const ITEM_INFORMATION_QUERY_KEY = 'itemInformation';

/**
 * Fetches item information records used for External SKU lookups.
 *
 * Note: this endpoint returns a large dataset; callers should consider limiting rendered option counts
 * when displaying results in searchable pickers.
 *
 * @param {Object} [options]
 * @param {boolean} [options.activeOnly=true] - Limits results to active item information when true.
 * @param {string} [options.itemType='itemMaster'] - Optional type filter supported by the API.
 * @param {boolean} [options.includeRecipeCards=false] - When false (default), omits L4 Recipe Cards.
 * @param {boolean} [options.enabled=true] - Enables the query when true.
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{externalSku:string,name:string}>, Error>}
 */
export const useItemInformationQuery = ({
    activeOnly = true,
    enabled = true,
    includeRecipeCards = false,
    itemType = 'itemMaster',
} = {}) =>
    useQuery({
        queryKey: [
            ITEM_INFORMATION_QUERY_KEY,
            activeOnly ? 'active' : 'all',
            itemType || 'allTypes',
            includeRecipeCards ? 'withRecipeCards' : 'noRecipeCards',
        ],
        enabled,
        queryFn: async () => {
            const response = await catalogApi.getItemInformation(activeOnly, itemType, {
                includeRecipeCards,
            });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch item information');
            }
            return Array.isArray(response?.data?.items) ? response.data.items : [];
        },
        staleTime: 15 * 60_000,
        gcTime: 30 * 60_000,
    });
